import {
  type ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AuthenticatedUser, SignInInput } from '#shared/types';
import { api, setForbiddenHandler, setUnauthorizedHandler } from '#client/lib/api';
import { ENDPOINT } from '#client/lib/endpoints';

interface AuthState {
  user: AuthenticatedUser | null;
  /** True until the initial `GET /auth/me` settles, so routes don't flash. */
  initializing: boolean;
  signIn: (input: SignInInput) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Submits this session's Day Close with an optional comment. The server ends
   * the session and clears the cookie, so this signs out too. Throws — leaving
   * the user signed in — if the submission is refused.
   */
  submitDayClose: (comment: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Resolve the session from the httpOnly cookie — the token is not readable
  // from JS by design, so the server is the source of truth.
  useEffect(() => {
    let active = true;

    api
      .get<AuthenticatedUser>(ENDPOINT.authMe)
      .then((me) => {
        if (active) setUser(me);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setInitializing(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // Any 401 from anywhere in the app drops the session.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
  }, []);

  // Permissions are resolved per request on the server, so an admin's change
  // applies at once there. These keep the screen in step: re-read who we are
  // when the tab regains focus, and when the server refuses something.
  useEffect(() => {
    let inFlight = false;
    const refresh = () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      api
        .get<AuthenticatedUser>(ENDPOINT.authMe)
        .then((me) => setUser((current) => (sameAccess(current, me) ? current : me)))
        // A 401 here already went through the handler above.
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
        });
    };
    setForbiddenHandler(refresh);
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const signIn = useCallback(async (input: SignInInput) => {
    setUser(await api.post<AuthenticatedUser>(ENDPOINT.authSignIn, input));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post(ENDPOINT.authSignOut, {});
    } finally {
      setUser(null);
    }
  }, []);

  const submitDayClose = useCallback(async (comment: string) => {
    await api.post(ENDPOINT.dayCloseSubmissions, { comment: comment.trim() || null });
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, initializing, signIn, signOut, submitDayClose }),
    [user, initializing, signIn, signOut, submitDayClose],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

/** Same person, same role, same permissions — so no re-render is needed. */
function sameAccess(a: AuthenticatedUser | null, b: AuthenticatedUser): boolean {
  return (
    a !== null &&
    a.id === b.id &&
    a.role.id === b.role.id &&
    a.role.name === b.role.name &&
    a.permissions.length === b.permissions.length &&
    a.permissions.every((p, i) => p === b.permissions[i])
  );
}

export function useAuth(): AuthState {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
